import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  Section,
  SectionInner,
  SectionHead,
  SectionHeading,
  SectionSubheading,
} from "../../globalStyles";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-6);

  @media screen and (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

/* 기사 하나가 스티커 카드 하나 */
const Article = styled.a`
  display: flex;
  flex-direction: column;
  padding: var(--space-5) var(--space-6) var(--space-6);
  border: 2px solid var(--text-primary);
  border-radius: var(--radius-lg);
  background: #ffffff;
  box-shadow: 4px 4px 0 var(--text-primary);
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translate(-2px, -2px);
    box-shadow: 6px 6px 0 var(--text-primary);
  }

  &:active {
    transform: translate(2px, 2px);
    box-shadow: 3px 3px 0 var(--text-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 4px;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
`;

const Source = styled.span`
  font-size: 0.8125rem;
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
`;

const Date = styled.span`
  font-size: 0.8125rem;
  color: var(--text-muted);
  white-space: nowrap;
`;

const Title = styled.p`
  font-size: 1rem;
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  line-height: 1.55;
  word-break: keep-all;
  text-wrap: pretty;
`;

const PressSection = () => {
  const [language, setLanguage] = useState("ko");
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/en") setLanguage("en");
    else if (location.pathname === "/ko") setLanguage("ko");
    else setLanguage(localStorage.getItem("language") || "ko");

    const handleLanguageChange = (e) => setLanguage(e.detail);
    window.addEventListener("languageChange", handleLanguageChange);
    return () =>
      window.removeEventListener("languageChange", handleLanguageChange);
  }, [location.pathname]);

  const heading = {
    ko: { title: "언론 속의 바삭한소프트", sub: "바삭한소프트의 이야기가 여러 매체를 통해 소개되었습니다." },
    en: {
      title: "Basakansoft in the press",
      sub: "Our story has been featured across a range of media outlets.",
    },
  };

  const articles = [
    {
      source: "디스이즈게임",
      date: "2026.06.03",
      title: "\"돈 없어서 마케팅 못하는 건 핑계\" 요즘 인디의 당찬 생존법 (누적 유저 150만) [인터뷰]",
      url: "https://www.thisisgame.com/articles/425141",
    },
    {
      source: "게임동아",
      date: "2026.05.27",
      title: "좀 더 매운맛으로 돌아왔다. '수험생키우기' 후속작 '취준생 키우기' (플레이엑스포2026)",
      url: "https://game.donga.com/122697/",
    },
    {
      source: "디지털투데이",
      date: "2026.01.29",
      title: "모바일 게임 '수험생 키우기' 개발한 바삭한소프트의 콘텐츠 철학 [인터뷰]",
      url: "https://www.digitaltoday.co.kr/news/articleView.html?idxno=624860",
    },
    {
      source: "게임동아",
      date: "2025.12.22",
      title: "대만 수험생들도 공감했다. '수험생키우기' 대만 무료 인기 1위",
      url: "https://game.donga.com/120799/",
    },
    {
      source: "머니투데이",
      date: "2025.12.16",
      title: "바삭한소프트, '수험생 키우기' 대만 앱스토어 1위 기록",
      url: "https://www.mt.co.kr/industry/2025/12/16/2025121515122672668",
    },
    {
      source: "게임동아",
      date: "2025.05.07",
      title: "시간이 지나도 따뜻한 추억이 되기를. '수험생키우기' 개발한 바삭한소프트 [인터뷰]",
      url: "https://game.donga.com/117181/",
    },
    {
      source: "머니투데이",
      date: "2024.12.13",
      title: "'수험생 키우기', 日 출시 2개월 만 다운 4만건 돌파",
      url: "http://news.mt.co.kr/mtview.php?no=2024121312182063721",
    },
    {
      source: "머니투데이",
      date: "2024.05.10",
      title: "'수험생 키우기' 엽차엽차, '바삭한소프트'로 사명 변경 완료",
      url: "http://news.mt.co.kr/mtview.php?no=2024051011192435116",
    },
    {
      source: "머니투데이",
      date: "2024.04.08",
      title: "'게임 개발' 바삭한소프트, '우수스타트업'으로 선정",
      url: "http://news.mt.co.kr/mtview.php?no=2024040817222893411",
    },
  ];

  const h = heading[language];

  return (
    <Section id="press" $rule>
      <SectionInner>
        <SectionHead>
          <SectionHeading>{h.title}</SectionHeading>
          <SectionSubheading>{h.sub}</SectionSubheading>
        </SectionHead>
        <Grid>
          {articles.map((a, i) => (
            <Article key={i} href={a.url} target="_blank" rel="noreferrer">
              <Meta>
                <Source>{a.source}</Source>
                <Date>{a.date}</Date>
              </Meta>
              <Title>{a.title}</Title>
            </Article>
          ))}
        </Grid>
      </SectionInner>
    </Section>
  );
};

export default PressSection;
